import { ZodType } from 'zod/v3';
import { ZodAlgebra, ZodUtils } from './index';
import { JSONSchema7 } from 'json-schema';

/**
 * JSON Schema type that represents the structure we need for LLM tool parameters
 */
export type JsonSchema = JSONSchema7;

/**
 * ZodAlgebra that converts Zod schemas to JSON Schema format suitable for LLM tool calls.
 * This algebra ensures that:
 * 1. All parameters are marked as "required" (for OpenAI compatibility)
 * 2. Nullable values are handled via type union with "null" (works with all providers)
 * 3. Optional fields are converted to nullable fields (since LLM tools can't be truly optional)
 */
const zodToJsonSchemaAlgebra: ZodAlgebra<JsonSchema> = {
  // Primitive types
  string: (schema) => {
    const def = schema._def;
    const result: JsonSchema = { type: 'string' };

    if (def.description) {
      result.description = def.description;
    }

    return result;
  },

  number: (schema) => {
    const def = schema._def;
    const result: JsonSchema = { type: 'number' };

    if (def.description) {
      result.description = def.description;
    }

    // Handle number constraints from checks
    if (def.checks !== undefined && Array.isArray(def.checks)) {
      for (const check of def.checks) {
        if (check.kind === 'min') {
          result.minimum = check.value;
        } else if (check.kind === 'max') {
          result.maximum = check.value;
        }
      }
    }

    return result;
  },

  boolean: (schema) => {
    const def = schema._def;
    const result: JsonSchema = { type: 'boolean' };

    if (def.description) {
      result.description = def.description;
    }

    return result;
  },

  null: () => ({ type: 'null' }),

  // Composite types
  array: (schema, element) => {
    const def = schema._def;
    const result: JsonSchema = {
      type: 'array',
      items: element,
    };

    if (def.description) {
      result.description = def.description;
    }

    return result;
  },

  object: (schema, properties) => {
    const def = schema._def;
    const result: JsonSchema = {
      type: 'object',
      properties: {},
      required: [],
    };

    if (def.description) {
      result.description = def.description;
    }

    // Convert properties and track required fields
    for (const [key, prop] of Object.entries(properties)) {
      const propSchema = prop as JsonSchema & { optional?: boolean };

      // For LLM tools, we treat all fields as required but allow null values
      // This satisfies OpenAI's requirement while working with all providers
      if (propSchema.optional) {
        // Convert optional to nullable by adding null to the type if not already present
        if (Array.isArray(propSchema.type)) {
          if (!propSchema.type.includes('null')) {
            propSchema.type = [...propSchema.type, 'null'];
          }
        } else if (propSchema.type === 'null') {
          // Already null, no need to change
        } else if (propSchema.type) {
          propSchema.type = [propSchema.type, 'null'];
        } else {
          propSchema.type = ['null'];
        }
        delete propSchema.optional;
      }

      result.properties![key] = propSchema;
      // All fields are required for LLM tools (OpenAI requirement)
      result.required!.push(key);
    }

    // Handle additional properties - default to false for LLM tools
    result.additionalProperties = false;

    return result;
  },

  union: (schema, types) => {
    const def = schema._def;

    // Check if this is a union of literals (enum-like)
    const allLiterals = types.every(
      (type) => type.type === 'string' && type.enum && type.enum.length === 1
    );

    if (allLiterals) {
      // Combine all literal values into a single enum
      const enumValues = types.flatMap((type) => type.enum || []);
      const result: JsonSchema = {
        type: 'string',
        enum: enumValues,
      };

      if (def.description) {
        result.description = def.description;
      }

      return result;
    }

    // Otherwise use oneOf
    const result: JsonSchema = { oneOf: types };

    if (def.description) {
      result.description = def.description;
    }

    return result;
  },

  intersection: (schema, left, right) => {
    const def = schema._def;
    const result: JsonSchema = { allOf: [left, right] };

    if (def.description) {
      result.description = def.description;
    }

    return result;
  },

  tuple: (schema, elements) => {
    const def = schema._def;
    const result: JsonSchema = {
      type: 'array',
      items: { oneOf: elements },
      minItems: elements.length,
      maxItems: elements.length,
    };

    if (def.description) {
      result.description = def.description;
    }

    return result;
  },

  enum: (schema) => {
    const def = schema._def;
    const result: JsonSchema = {
      type: 'string',
      enum: def.values,
    };

    if (def.description) {
      result.description = def.description;
    }

    return result;
  },

  nativeEnum: (schema) => {
    const def = schema._def;
    const result: JsonSchema = {
      type: 'string',
      enum: Object.values(def.values),
    };

    if (def.description) {
      result.description = def.description;
    }

    return result;
  },

  literal: (schema) => {
    const def = schema._def;
    const value = def.value;

    if (typeof value === 'string') {
      return { type: 'string', enum: [value] };
    } else if (typeof value === 'number') {
      return { type: 'number', enum: [value] };
    } else if (typeof value === 'boolean') {
      return { type: 'boolean', enum: [value] };
    } else {
      return { type: 'null', enum: [null] };
    }
  },

  date: (schema) => {
    const def = schema._def;
    const result: JsonSchema = {
      type: 'string',
      format: 'date-time',
    };

    if (def.description) {
      result.description = def.description;
    }

    return result;
  },

  // Wrapper types
  nullable: (schema, inner) => {
    const def = schema._def;
    const result = { ...inner };

    if (def.description) {
      result.description = def.description;
    }

    // Add null to the type union if it's not already present
    if (Array.isArray(result.type)) {
      if (!result.type.includes('null')) {
        result.type = [...result.type, 'null'];
      }
    } else if (result.type === 'null') {
      // Already null, no need to change
    } else if (result.type) {
      result.type = [result.type, 'null'];
    } else {
      result.type = ['null'];
    }

    return result;
  },

  optional: (schema, inner) => {
    // For LLM tools, we convert optional to nullable since tools can't be truly optional
    // This ensures all fields are "required" but allows null values
    const def = schema._def;
    const result = { ...inner };

    if (def.description) {
      result.description = def.description;
    }

    // Add null to the type union if it's not already present
    if (Array.isArray(result.type)) {
      if (!result.type.includes('null')) {
        result.type = [...result.type, 'null'];
      }
    } else if (result.type === 'null') {
      // Already null, no need to change
    } else if (result.type) {
      result.type = [result.type, 'null'];
    } else {
      result.type = ['null'];
    }

    return result;
  },

  default: (schema, inner) => {
    const def = schema._def;
    const result = { ...inner };

    if (def.description) {
      result.description = def.description;
    }

    // Add default value if it's a primitive
    if (def.defaultValue !== undefined) {
      result.default = def.defaultValue();
    }

    return result;
  },

  effects: (schema, inner) => {
    const def = schema._def;
    const result = { ...inner };

    if (def.description) {
      result.description = def.description;
    }

    // Handle validation constraints from the original schema
    const originalDef = (schema as any)._def;
    if (originalDef.checks && Array.isArray(originalDef.checks)) {
      for (const check of originalDef.checks) {
        if (check.kind === 'min') {
          if (result.type === 'string') {
            result.minLength = check.value;
          } else if (result.type === 'number') {
            result.minimum = check.value;
          }
        } else if (check.kind === 'max') {
          if (result.type === 'string') {
            result.maxLength = check.value;
          } else if (result.type === 'number') {
            result.maximum = check.value;
          }
        } else if (check.kind === 'regex') {
          if (result.type === 'string') {
            result.pattern = check.regex.source;
          }
        }
      }
    }

    return result;
  },

  // Types that are not supported for LLM tool calls
  any: () => ({ type: 'string' }), // Fallback to string
  unknown: () => ({ type: 'string' }), // Fallback to string
  void: () => ({ type: 'null' }), // Fallback to null
  undefined: () => ({ type: 'null' }), // Fallback to null
  never: () => ({ not: {} }), // Impossible type
  bigint: () => ({ type: 'string' }), // Fallback to string
  symbol: () => ({ type: 'string' }), // Fallback to string
  record: () => ({ type: 'object', additionalProperties: { type: 'string' } }), // Fallback to object
  map: () => ({ type: 'object', additionalProperties: { type: 'string' } }), // Fallback to object
  set: () => ({ type: 'array', items: { type: 'string' } }), // Fallback to array
  function: () => ({ type: 'string' }), // Fallback to string
  promise: () => ({ type: 'string' }), // Fallback to string
  lazy: () => ({ type: 'string' }), // Fallback to string
};

/**
 * Converts a Zod schema to JSON Schema format suitable for LLM tool calls.
 * This function ensures compatibility with all LLM providers by:
 * 1. Making all parameters "required" (OpenAI requirement)
 * 2. Converting optional fields to nullable fields
 * 3. Handling nullable values via type unions with "null"
 *
 * @param schema - The Zod schema to convert
 * @returns JSON Schema object suitable for LLM tool parameters
 */
export const zodToJsonSchema = (schema: ZodType): JsonSchema => {
  return ZodUtils.fold(zodToJsonSchemaAlgebra, schema);
};

/**
 * Type guard to check if a JSON Schema is valid for LLM tool calls
 */
export const isValidToolJsonSchema = (jsonSchema: JsonSchema): boolean => {
  // Must be an object type
  if (jsonSchema.type !== 'object') {
    return false;
  }

  // Must have properties
  if (
    !jsonSchema.properties ||
    Object.keys(jsonSchema.properties).length === 0
  ) {
    return false;
  }

  // All properties must be in required array
  if (
    !jsonSchema.required ||
    jsonSchema.required.length !== Object.keys(jsonSchema.properties).length
  ) {
    return false;
  }

  return true;
};
